<template>
  <el-dialog
    :visible="visible"
    title="編輯匹配記錄"
    width="920px"
    append-to-body
    :close-on-click-modal="false"
    @update:visible="handleVisibleChange"
    @closed="handleClosed"
  >
    <div v-loading="previewing || submitting" class="pos-id-edit">
      <b-alert v-if="readOnly" variant="warning" show>{{ readOnlyHint }}</b-alert>

      <b-alert v-if="submittedData" variant="success" show>
        提交成功：後端已寫入變更記錄（audit_id={{ submittedData.audit_id }}）。
      </b-alert>

      <b-alert v-if="errorInfo" variant="danger" show>
        <div class="font-weight-bold">{{ errorInfo.title }}</div>
        <div>{{ errorInfo.message }}</div>
        <div v-if="errorInfo.backendMessage">
          後端訊息：{{ errorInfo.backendMessage }}
        </div>
        <div v-if="errorInfo.code === 10060" class="mt-2">
          <b-button size="sm" variant="outline-primary" @click="locateConflictRecord">
            跳至該筆記錄（以 pos-id 精確查詢）
          </b-button>
        </div>
        <div v-if="errorInfo.code === 10061 && !form.force" class="mt-2">
          <b-button size="sm" variant="outline-warning" @click="applyForce">
            勾選「強制略過校驗」後重新預覽
          </b-button>
        </div>
      </b-alert>

      <div class="text-muted small mb-2">
        記錄 #{{ recordId }} · {{ recordCircuitLabel }} · 目前 pos-id {{ recordPosId }}
      </div>

      <b-row>
        <b-col md="6">
          <b-form-group label="pos-id" label-size="sm">
            <b-form-input
              v-model="form.pos_id"
              placeholder="例：13527（正規化後不得超過 15 字元）"
            />
            <small class="text-muted">
              改號只動匹配記錄；要連帶搬移歷史票房請在下方高階區選「連帶改掛歷史票房」。
            </small>
          </b-form-group>

          <b-form-group label="film_id（影片 id）" label-size="sm">
            <b-form-input v-model="form.film_id" placeholder="例：5013" />
          </b-form-group>

          <b-form-group label="film_master_id（master id）" label-size="sm">
            <b-form-input v-model="form.film_master_id" placeholder="不填時後端自動取該影片的 master" />
          </b-form-group>

          <b-form-group label="title_name（標注的影片標題）" label-size="sm">
            <b-form-input v-model="form.title_name" />
            <small class="text-muted">
              預設只改匹配記錄標題；勾選下方「回寫標注的影片標題」才會改歷史票房記錄的標題。
            </small>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="影片選擇器（搜尋片名 → 選一個版本）" label-size="sm">
            <b-input-group>
              <b-form-input
                v-model="filmSearch"
                placeholder="例：DDDD"
                @keyup.enter="searchFilms"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-info"
                  :disabled="filmSearchLoading"
                  @click="searchFilms"
                >
                  搜尋影片
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <div v-if="filmSearchLoading" class="text-muted small mt-1">搜尋中…</div>
            <div
              v-else-if="filmSearchDone && filmMasters.length === 0"
              class="text-muted small mt-1"
            >
              沒有符合的影片。
            </div>
            <div
              v-if="filmMasters.length > 0"
              class="border rounded mt-2 p-2"
              style="max-height: 260px; overflow: auto"
            >
              <div v-for="master in filmMasters" :key="masterKey(master)" class="mb-2">
                <div class="small font-weight-bold">{{ masterLabel(master) }}</div>
                <b-button
                  v-for="child in masterChildren(master)"
                  :key="filmKey(child)"
                  size="sm"
                  class="mr-1 mb-1"
                  :variant="isPicked(child) ? 'primary' : 'outline-primary'"
                  @click="pickFilm(child)"
                >
                  {{ child.id }} · {{ child.title_name }}
                </b-button>
              </div>
            </div>
            <small class="text-muted">
              選一個版本會自動帶出 film_id / master_id / title_name，仍可手動改 film_id。
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <div class="border rounded p-2 mb-3 bg-light">
        <h6 class="mb-2">高階選項</h6>
        <b-form-group label="歷史票房變更" label-size="sm" class="mb-2">
          <b-form-select v-model="form.carry_over" :options="carryOverOptions" />
        </b-form-group>
        <b-row v-if="form.carry_over === 'engagements'">
          <b-col md="6">
            <b-form-group label="涉及票房起始日（date_start）" label-size="sm">
              <b-form-input v-model="form.date_start" type="date" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="涉及票房結束日（date_end）" label-size="sm">
              <b-form-input v-model="form.date_end" type="date" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-checkbox v-model="form.sync_engagement_title_name" class="mb-1">
          回寫標注的影片標題
        </b-form-checkbox>
        <b-form-checkbox v-model="form.create_mapping" class="mb-1">
          同時寫入重定向映射
        </b-form-checkbox>
<!--        <b-form-checkbox v-model="form.force" class="mb-1">-->
<!--          強制略過影片 / master 校驗（force，越過 10061）-->
<!--        </b-form-checkbox>-->
        <small class="text-muted">
          範圍承諾：遷移與標題回寫只影響「該行自己的影片 + 本院線 + 該 pos-id」命中的記錄。
        </small>
        <br />
      </div>

      <b-form-group label="緣由（reason，正式提交必填）" label-size="sm">
        <b-form-input v-model="form.reason" maxlength="255" placeholder="例：馬拉松後章歸位" />
      </b-form-group>

      <div v-if="summaryData" class="mt-3">
        <div v-if="autoPreviewing" class="pos-id-hint mb-1">
          <i class="el-icon-loading mr-1"></i>表單已變更，正在自動更新預覽與連帶影響數字…
        </div>
        <change-summary :data="summaryData" />
      </div>

      <b-alert
        v-if="!submittedData && previewData && isHighImpact"
        variant="warning"
        show
        class="mt-2"
      >
        <div class="font-weight-bold">高影響操作，請逐條確認：</div>
        <ul class="mb-2">
          <li v-for="(reason, index) in impactReasons" :key="index">{{ reason }}</li>
        </ul>
        <b-form-checkbox v-model="confirmHighImpact">
          我已了解上述影響，確認執行
        </b-form-checkbox>
      </b-alert>

      <b-alert
        v-if="!submittedData && previewData && !previewUpToDate"
        variant="secondary"
        show
        class="mt-2"
      >
        表單已變更，連帶影響數字會自動更新；如未更新，請重新按「預覽」後再提交。
      </b-alert>
    </div>

    <template #footer>
      <div class="text-right">
        <b-button size="sm" variant="outline-secondary" @click="handleVisibleChange(false)">
          取消
        </b-button>
        <b-button
          size="sm"
          variant="outline-info"
          class="ml-2"
          :disabled="busy || readOnly || submittedData !== null"
          @click="preview"
        >
          預覽
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          class="ml-2"
          :disabled="!canSubmit"
          @click="submit"
        >
          確認提交
        </b-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { Message } from 'element-ui'
import { updateFilmCircuitId } from '@/api/posId'
import { getFilmRunListOfFilm } from '@/api/film-material-management/film'
import {
  buildFilmCircuitIdUpdatePayload,
  highImpactReasons,
  isHighImpactUpdate,
  POS_ID_READ_ONLY_HINT,
  resolvePosIdError
} from '@/utils/posId'
import ChangeSummary from './ChangeSummary.vue'

const CARRY_OVER_OPTIONS = [
  { text: '只改匹配記錄，不改動相關票房', value: 'none' },
  { text: '遷移關聯在記錄的票房', value: 'engagements' }
]

function hasText(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function firstText(...values) {
  for (let index = 0; index < values.length; index++) {
    if (hasText(values[index])) {
      return String(values[index])
    }
  }
  return ''
}

function emptyForm() {
  return {
    pos_id: '',
    film_id: '',
    film_master_id: '',
    title_name: '',
    sync_engagement_title_name: false,
    carry_over: 'none',
    date_start: '',
    date_end: '',
    create_mapping: false,
    force: false,
    reason: ''
  }
}

/**
 * P3 編輯表單（guide §2.3 / §5 P3）：
 * 填表 → 預覽（dry_run=true，必看 changes / carry_over / title_sync / notes）
 * → 高影響二次確認（逐條列出 highImpactReasons 並要求勾選）→ 正式提交（reason 必填）。
 */
export default {
  name: 'FilmCircuitIdEditDialog',
  components: {
    ChangeSummary
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    record: {
      type: Object,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: emptyForm(),
      filmSearch: '',
      filmMasters: [],
      filmSearchLoading: false,
      filmSearchDone: false,
      previewData: null,
      previewSignature: '',
      previewing: false,
      autoPreviewing: false,
      autoPreviewTimer: null,
      submitting: false,
      submittedData: null,
      confirmHighImpact: false,
      errorInfo: null,
      readOnlyHint: POS_ID_READ_ONLY_HINT,
      carryOverOptions: CARRY_OVER_OPTIONS
    }
  },
  computed: {
    recordId() {
      return this.record && this.record.id !== undefined ? this.record.id : ''
    },
    recordPosId() {
      return this.record && hasText(this.record.pos_id) ? String(this.record.pos_id) : '—'
    },
    recordCircuitLabel() {
      const row = this.record
      if (!row) {
        return '—'
      }
      const name = hasText(row.circuit_name) ? String(row.circuit_name) : ''
      const id = hasText(row.circuit_id) ? String(row.circuit_id) : ''
      return name && id ? `${name}（${id}）` : name || id || '—'
    },
    formSignature() {
      const payload = buildFilmCircuitIdUpdatePayload(this.form, true)
      // reason 只是審計文字，不影響 dry_run 預覽結果（changes / 連帶影響條數都不變）；
      // 不列入簽名，避免「預覽後才填緣由」被誤判為表單已變更、或一直觸發自動重跑預覽。
      delete payload.reason
      return JSON.stringify(payload)
    },
    previewUpToDate() {
      return this.previewData !== null && this.previewSignature === this.formSignature
    },
    isHighImpact() {
      return isHighImpactUpdate(this.form)
    },
    impactReasons() {
      return highImpactReasons(this.form)
    },
    busy() {
      return this.previewing || this.autoPreviewing || this.submitting
    },
    summaryData() {
      return this.submittedData ? this.submittedData : this.previewData
    },
    canSubmit() {
      if (this.readOnly || this.busy || this.submittedData !== null) {
        return false
      }
      if (this.previewData === null || !this.previewUpToDate) {
        return false
      }
      if (this.isHighImpact && !this.confirmHighImpact) {
        return false
      }
      return true
    }
  },
  watch: {
    visible(value) {
      if (value === true) {
        this.resetByRecord()
      }
    },
    record() {
      if (this.visible === true) {
        this.resetByRecord()
      }
    },
    // 已有預覽結果後，勾選項 / 欄位一變更就自動重跑 dry_run 預覽：
    // 讓「連帶影響」的條數（含 title_sync.finals_updated / hourlies_updated）即時刷新，
    // 不需要每次手動再按一次「預覽」。
    formSignature() {
      this.scheduleAutoPreview()
    }
  },
  beforeDestroy() {
    this.clearAutoPreviewTimer()
  },
  methods: {
    handleVisibleChange(value) {
      this.$emit('update:visible', value === true)
    },
    handleClosed() {
      this.resetPreview()
      this.submittedData = null
      this.errorInfo = null
    },
    resetPreview() {
      this.clearAutoPreviewTimer()
      this.autoPreviewing = false
      this.previewData = null
      this.previewSignature = ''
      this.confirmHighImpact = false
    },
    resetByRecord() {
      const row = this.record || {}
      this.form = {
        pos_id: hasText(row.pos_id) ? String(row.pos_id) : '',
        film_id: hasText(row.film_id) ? String(row.film_id) : '',
        film_master_id: firstText(row.film_master_id, row.master_id),
        title_name: row.title_name === undefined || row.title_name === null ? '' : String(row.title_name),
        sync_engagement_title_name: false,
        carry_over: 'none',
        date_start: '',
        date_end: '',
        create_mapping: false,
        force: false,
        reason: ''
      }
      this.resetPreview()
      this.submittedData = null
      this.errorInfo = null
      this.filmSearch = ''
      this.filmMasters = []
      this.filmSearchDone = false
      this.filmSearchLoading = false
    },
    masterChildren(master) {
      return master && Array.isArray(master.children) ? master.children : []
    },
    masterLabel(master) {
      if (!master) {
        return '—'
      }
      const name = hasText(master.name_zh) ? master.name_zh : master.name_en
      return hasText(name) ? `master ${master.id} · ${name}` : `master ${master.id}`
    },
    masterKey(master) {
      return `master-${master && master.id !== undefined ? master.id : 'x'}`
    },
    filmKey(child) {
      return `film-${child && child.id !== undefined ? child.id : 'x'}`
    },
    isPicked(child) {
      return Boolean(child) && String(this.form.film_id) === String(child.id)
    },
    searchFilms() {
      const keyword = hasText(this.filmSearch) ? String(this.filmSearch).trim() : ''
      if (!keyword) {
        Message.warning('請先輸入片名關鍵字')
        return
      }
      this.filmSearchLoading = true
      this.filmSearchDone = false
      getFilmRunListOfFilm({ search: keyword })
        .then((res) => {
          const list = res && Array.isArray(res.data) ? res.data : []
          this.filmMasters = list
          this.filmSearchLoading = false
          this.filmSearchDone = true
          if (list.length === 0) {
            Message.info('沒有符合的影片，請換個關鍵字')
          }
        })
        .catch((error) => {
          this.filmSearchLoading = false
          this.filmSearchDone = true
          const info = resolvePosIdError(error)
          Message.error(info.message)
        })
    },
    pickFilm(child) {
      if (!child) {
        return
      }
      if (hasText(child.id)) {
        this.form.film_id = String(child.id)
      }
      if (hasText(child.master_id)) {
        this.form.film_master_id = String(child.master_id)
      }
      if (hasText(child.title_name)) {
        this.form.title_name = String(child.title_name)
      }
      Message.info(`已帶出影片 ${this.form.film_id} / master ${this.form.film_master_id}，請確認後再預覽`)
    },
    applyForce() {
      this.form.force = true
      Message.info('已勾選「強制略過校驗」，請重新預覽後再提交')
    },
    locateConflictRecord() {
      const target = hasText(this.form.pos_id) ? String(this.form.pos_id).trim() : String(this.recordPosId)
      this.$emit('locate', { pos_id: target })
    },
    preview() {
      this.clearAutoPreviewTimer()
      this.runPreview(false)
    },
    /**
     * dry_run 預覽（手動與自動刷新共用）。
     * silent=true 由 watcher 觸發：不彈錯誤、不顯示全屏 loading，只小字提示「正在更新」。
     */
    runPreview(silent) {
      if (this.readOnly) {
        if (silent !== true) {
          Message.warning(this.readOnlyHint)
        }
        return
      }
      if (this.previewing || this.autoPreviewing || this.submitting) {
        return
      }
      if (!hasText(this.recordId)) {
        return
      }
      const signature = this.formSignature
      const requestRecordId = this.recordId
      if (silent === true) {
        this.autoPreviewing = true
      } else {
        this.errorInfo = null
        this.previewing = true
      }
      updateFilmCircuitId(requestRecordId, buildFilmCircuitIdUpdatePayload(this.form, true))
        .then((res) => {
          this.previewing = false
          this.autoPreviewing = false
          // 回應回來前若已切換記錄 / 關閉對話框，丟棄這份預覽，避免舊資料蓋掉新狀態
          if (String(this.recordId) !== String(requestRecordId) || this.visible !== true) {
            return
          }
          const data = res && res.data ? res.data : null
          if (!data) {
            return
          }
          this.previewData = data
          this.previewSignature = signature
          this.confirmHighImpact = false
          if (silent !== true) {
            const changes = Array.isArray(data.changes) ? data.changes : []
            if (changes.length === 0) {
              Message.warning('後端未回傳任何欄位變更，請確認是否真的改了欄位')
            }
          }
          // 自動預覽請求期間表單又變了 → 再排一次，確保連帶影響數字最終與表單一致
          if (this.formSignature !== this.previewSignature) {
            this.scheduleAutoPreview()
          }
        })
        .catch((error) => {
          this.previewing = false
          this.autoPreviewing = false
          if (silent === true) {
            // 自動更新失敗時不打擾使用者：保留上一份預覽，等下次變更或手動按「預覽」
            return
          }
          this.resetPreview()
          this.handleError(error)
        })
    },
    scheduleAutoPreview() {
      this.clearAutoPreviewTimer()
      if (this.visible !== true || this.submittedData !== null) {
        return
      }
      if (this.previewData === null || this.previewing || this.autoPreviewing || this.submitting) {
        return
      }
      this.autoPreviewTimer = setTimeout(() => {
        this.autoPreviewTimer = null
        this.runPreview(true)
      }, 600)
    },
    clearAutoPreviewTimer() {
      if (this.autoPreviewTimer !== null) {
        clearTimeout(this.autoPreviewTimer)
        this.autoPreviewTimer = null
      }
    },
    submit() {
      if (this.readOnly) {
        Message.warning(this.readOnlyHint)
        return
      }
      if (!hasText(this.form.reason)) {
        Message.warning('請填寫「緣由（reason）」，正式提交時必填且會寫入變更記錄')
        return
      }
      if (this.previewData === null) {
        Message.warning('請先按「預覽」確認變更內容')
        return
      }
      if (!this.previewUpToDate) {
        Message.warning('表單已變更，請重新按「預覽」後再提交')
        return
      }
      if (this.isHighImpact && !this.confirmHighImpact) {
        Message.warning('這是高影響操作，請勾選確認後再提交')
        return
      }
      this.errorInfo = null
      this.submitting = true
      updateFilmCircuitId(this.recordId, buildFilmCircuitIdUpdatePayload(this.form, false))
        .then((res) => {
          this.submitting = false
          const data = res && res.data ? res.data : null
          this.submittedData = data
          if (data) {
            Message.success(`已寫入變更記錄（audit_id=${data.audit_id}）`)
          }
          this.$emit('submitted', data)
        })
        .catch((error) => {
          this.submitting = false
          this.handleError(error)
        })
    },
    handleError(error) {
      const info = resolvePosIdError(error)
      let title = '操作失敗'
      if (info.code === 10060) {
        title = '該 pos-id 已匹配到這部影片'
      } else if (info.code === 10061) {
        title = '影片與 master 不匹配'
      } else if (info.code === 10040) {
        title = '輸入不合法'
      } else if (info.isPermissionDenied) {
        title = '沒有操作權限（本模組需要 server-admin）'
      } else if (info.isNetworkError) {
        title = '網絡異常'
      }
      this.errorInfo = {
        code: info.code,
        title,
        message: info.message,
        backendMessage: info.backendMessage
      }
      Message.error(`${title}：${info.message}`)
      if (info.isPermissionDenied) {
        this.$emit('read-only')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.pos-id-edit {
  h6 {
    color: #0e72bc;
    font-weight: 500;
  }
}
</style>
